import { useEffect, useState } from "react";
import fetchPostData from "./UAFetchPostData";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Share2, MoreHorizontal, Bookmark, Archive, Flag, Monitor } from "lucide-react";
import "../../Styles/UAPostContent.css";
import Carousel from "../../../../common/Carousel/Carousel";
import GridLayout from "../../../../common/Grid/gridMode";
import LightboxGallery from "../../../../common/LightBox/lightBox";
import HorizontalScrollDisplay from "../../../../common/HorizontalScroll/horizontalScroll";
import VerticalScroll from "../../../../common/VerticalScroll/verticalScroll";
import "../../../Styles/common-styles.css";
import "../../Styles/UACommonStyles.css";

const UserAccountShowAllPost = ({ mediaDisplayMode }) => {
  const [postData, setPostData] = useState([]);
  const [hasProfile, setHasProfile] = useState(false);
  const [likedPosts, setLikedPosts] = useState({});

  useEffect(() => {
    fetchPostData().then((data) => setPostData(data));
  }, []);

  const handlePostLike = (postId) => {
    setLikedPosts((prev) => ({
      ...prev,
      [postId]: !prev[postId],
    }));
  };

  useEffect(() => {
    const saved = localStorage.getItem("features");
    try {
      const features = saved ? JSON.parse(saved) : [];
      setHasProfile(features.includes("IDN"));
    } catch (err) {
      console.error("Invalid feature format in localStorage", err);
    }
  }, []);

    // Helper function to get comment count
    const getCommentCount = (comments) => {
      if (!comments) return 0;
      if (Array.isArray(comments)) return comments.length;
      return 0;
    };
  
    // ✅ Helper function to safely get likes count
    const getLikesCount = (likes) => {
      if (typeof likes === "number") return likes;
      if (typeof likes === "object" && likes !== null && likes.count) {
        return typeof likes.count === "number" ? likes.count : 0;
      }
      return 0;
    };
  
    // ✅ Helper function to safely get shares count
    const getSharesCount = (shares) => {
      if (typeof shares === "number") return shares;
      if (typeof shares === "object" && shares !== null && shares.count) {
        return typeof shares.count === "number" ? shares.count : 0;
      }
      return 0;
    };

  return (
    <div className="uacp-post-container" style={{marginBottom: 100, background: "rgba(36,36,45,0.2)"}}>
      {postData.length === 0 ? (
        <p className="text-label uacp-loading">Loading...</p>
      ) : (
        postData.map((userPost) => (
          <div key={userPost.useruuid} className="uacp-user-feed">
            {userPost.posts.map((post) => (
              <div key={post.postId} className="uacp-post-card"  style={{background: "rgba(36,36,45,0.2)"}}>
                {/* User Header */}
                <div className="uacp-user-container">
                  <div className="uacp-user-info-wrapper">
                    <div className="uacp-user-avatar-wrapper">
                      <img
                        src={userPost.userimage}
                        alt="Post"
                        className="tt-profile-image"
                      />
                    </div>

                    <div className="uacp-user-name-wrapper">
                      <p className="text-header">
                        <Link
                          to={`/user-account/${userPost.userId}`}
                          className="uacp-user-link"
                          style={{color: "white"}}
                        >
                          {userPost.userId}
                        </Link>
                      </p>
                    </div>

                    {/* Posted On Profile - Highlighted */}
                    {userPost.profileId && (
                      <div className="uacp-profile-badge" style={{background: "rgba(36,36,45,0.2)", color: "white", border: "1px solid white"}}>
                        {hasProfile ? (
                          <Link
                            to={`/user-page/${userPost.userId}/${userPost.profileId}`}
                            className="uacp-profile-link"
                          >
                            Posted On: <span className="uacp-profile-name">{userPost.profileId}</span>
                          </Link>
                        ) : (
                          <span className="uacp-profile-disabled">
                            Posted On: {userPost.profileId}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Location & Timestamp */}
                <div className="uacp-location-time-wrapper">
                  <div className="uacp-feed-header">
                    <p className="text-label">
                      <Link
                        to={`/location-page/${post.location}`}
                        className="uacp-location-link"
                        style={{color: "white"}}
                      >
                        {post.location}
                      </Link>
                      , {new Date(post.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>

                {/* Post Media */}
                <div className="uacp-feed-media">
                  <p className="text-label uacp-post-description" style={{marginLeft: 10}}>
                    {post.descriptions}
                  </p>
                  {Array.isArray(post.medias) && post.medias.length > 0 && (
                    <div className="uacp-feed-media-container">
                      {mediaDisplayMode === "slideshow" ? (
                        <Carousel items={post.medias} />
                      ) : mediaDisplayMode === "lightbox" ? (
                        <LightboxGallery mediaFiles={post.medias} />
                      ) : mediaDisplayMode === "horizontal-scroll" ? (
                        <HorizontalScrollDisplay medias={post.medias} />
                      ) : mediaDisplayMode === "vertical-scroll" ? (
                        <VerticalScroll medias={post.medias} />
                      ) : (
                        <GridLayout mediaFiles={post.medias} />
                      )}
                    </div>
                  )}
                </div>

                {/* Likes, Shares, Suggestions */}
                <div className="uacp-post-stats-wrapper" style={{ marginTop: 0, display: "flex", flexDirection: "row", marginLeft: 14}}>
                <button
                    onClick={() => handlePostLike(post.postId)}
                    className={`flex items-center gap-1.5 text-sm transition-colors ${
                      likedPosts[post.postId] ? "text-red-500" : "text-muted-foreground hover:text-red-500"
                    }`}
                    style={{color: "white"}}
                  >
                    <Heart 
                      size={17} 
                      fill={likedPosts[post.postId] ? "currentColor" : "none"} 
                      color={likedPosts[post.postId] ? "red" : "currentColor"}
                    />
                    {getLikesCount(post.likes)}
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    style={{color: "white"}}
                  >
                    <MessageCircle size={17} />
                    {getCommentCount(post.comments)}
                  </button>
                  <button className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
                    style={{color: "white"}}
                  >
                    <Share2 size={17} />
                    {getSharesCount(post.shares)}
                  </button>
                  <div className="uacf-post-stats-right" style={{marginLeft: 270}}>
                    <span className="text-label uacf-impressions-text">
                      Gained {post.impressions || 100} impressions 👀 in last 10 hours
                    </span>
                  </div>
                </div>

                {/* Comments Section */}
                <div className="uacp-comments-wrapper">
                  <div className="uacp-feed-comments">
                    <p className="uacp-comments-title" style={{color: "white", marginLeft: 15}}>Comments:</p>
                    <div className="uacp-comments-container">
                      {Array.isArray(post.comments) && post.comments.length > 0 ? (
                        post.comments.map((comment, index) => (
                          <div key={index} className="uacp-comment-item" style={{marginLeft: 15}}>
                            <p className="text-label">
                              <Link
                                to={`/user-account/${comment.userId}`}
                                className="uacp-comment-link"
                                style={{color: "white"}}
                              >
                                {comment.userId}: {comment.comment} - (
                                {new Date(comment.timestamp).toLocaleString()})
                              </Link>
                            </p>
                          </div>
                        ))
                      ) : (
                        <p className="uacp-no-comments">No comments yet.</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ))
      )}
    </div>
  );
};

export default UserAccountShowAllPost;
